import React, { useState } from 'react';

export interface DreamReviewInsight {
  id: string;
  content: string;
  created_at: string | null;
  night: string | null;
  run_id?: string;
  revision?: string;
  population?: string;
  topic?: string;
  stance?: string;
  about?: string;
  replacement_reason?: string;
  evidence_status?: string;
  claim_grade?: string;
  supported_fact?: { kind?: string; speaker?: string };
  held_out?: boolean;
  retirement_reason?: string;
  eligible_for_retention?: boolean;
  review?: { status: string; decision_id?: string; reason?: string; decided_at?: string };
  retention?: { status: string; reason?: string; receipt?: { record_id: string } };
  sources?: Array<{ id: string; kind?: string; author?: string; date?: string; date_role?: string; grade?: string;
    owner_words?: Array<{ speaker: string; text: string }>; trade_facts?: Record<string, unknown> }>;
}

type Props = {
  insight: DreamReviewInsight;
  onReview: (action: 'confirm' | 'reject', reason: string) => Promise<void>;
};

/** Existing Dream row's owner controls. No decision or retention is optimistic. */
export default function ArthurDreamReview({ insight, onReview }: Props) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const review = insight.review?.status || 'pending';
  const retention = insight.retention?.status || 'not_requested';
  const closed = review === 'rejected' || review === 'retired' || !!insight.retirement_reason;
  const submit = async (action: 'confirm' | 'reject') => {
    setError('');
    if (action === 'reject' && !reason.trim()) {
      setError('Enter the reason for rejecting this point.');
      return;
    }
    setBusy(true);
    try {
      await onReview(action, action === 'reject' ? reason.trim() : '');
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Decision not saved. Refresh before retrying.');
    } finally {
      setBusy(false);
    }
  };
  const control: React.CSSProperties = {
    background: '#141414', color: '#ededed', border: '1px solid #a77827',
    borderRadius: 6, padding: '6px 10px', cursor: busy ? 'wait' : 'pointer',
    boxShadow: 'inset 0 1px 0 #ffffff0d, 0 2px 6px #00000066',
  };
  return <div style={{ marginTop: 10, fontSize: 11, color: '#b7b7b7' }}>
    <div role="status">
      {insight.evidence_status === 'source_bound' ? 'Source-backed report' : 'Unverified proposal'}
      {' · '}{insight.population === 'normal_live' ? 'Live' : `Practice / ${insight.population || 'context unknown'}`}
      {' · Review: '}{insight.retirement_reason ? 'retired' : review}
      {' · Retention: '}{retention.replace(/_/g, ' ')}
    </div>
    {insight.topic && <div>Topic: {insight.topic} · {insight.stance} · about {insight.about}</div>}
    {insight.supported_fact?.kind === 'owner_report' && <div>Attributed owner words; source transcript details below.</div>}
    {insight.replacement_reason && <div>Replacement reason: {insight.replacement_reason}</div>}
    {insight.held_out && <div>This point is excluded from current understanding.</div>}
    {(insight.review?.reason || insight.retirement_reason) &&
      <div>Reason: {insight.review?.reason || insight.retirement_reason}</div>}
    {insight.retention?.reason && <div>Retention: {insight.retention.reason}</div>}
    <details style={{ margin: '6px 0' }}>
      <summary>Evidence and identity</summary>
      <div>Insight: {insight.id} · Run: {insight.run_id || 'unknown'}</div>
      <div>Recorded: {insight.created_at || 'unknown'} · Reviewed: {insight.review?.decided_at || 'not reviewed'}</div>
      {insight.retention?.receipt && <div>Retained memory: {insight.retention.receipt.record_id}</div>}
      {(insight.sources || []).map((source, i) => <div key={`${source.id}:${i}`}>
        {source.id} · {source.kind || 'kind unknown'} · {source.author || 'author unknown'}
        {' · '}{source.date || 'date unknown'} ({source.date_role || 'date role unknown'})
        {' · source grade: '}{source.grade || 'UNGRADED'}
        {(source.owner_words || []).map((statement, n) =>
          <blockquote key={n}>{statement.speaker}: {statement.text}</blockquote>)}
        {!!Object.keys(source.trade_facts || {}).length &&
          <div>Recorded trade facts (confirmation does not change these):
            <dl>{Object.entries(source.trade_facts || {}).map(([key, value]) =>
              <React.Fragment key={key}><dt>{key}</dt><dd>{value === null ? 'unknown' : String(value)}</dd></React.Fragment>)}</dl>
          </div>}
      </div>)}
    </details>
    {!closed && <>
      <div style={{ marginBottom: 6 }}>
        Confirm approves the exact point above for attempted personal-experience retention.
        It does not verify a broker fill, change a trade or promote doctrine.
      </div>
      <button type="button" style={control}
        disabled={busy || !insight.revision || !insight.eligible_for_retention || retention === 'retained'}
        onClick={() => submit('confirm')}>
        {review === 'confirmed' ? 'RETRY RETENTION' : 'CONFIRM'}
      </button>
      <label style={{ display: 'block', marginTop: 8 }}>
        Rejection reason
        <textarea aria-label="Rejection reason" value={reason}
          onChange={e => setReason(e.target.value)} disabled={busy}
          style={{ ...control, display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4 }} />
      </label>
      <button type="button" style={{ ...control, marginTop: 6 }}
        disabled={busy || !insight.revision} onClick={() => submit('reject')}>REJECT</button>
      {!insight.eligible_for_retention && <div>This point is not eligible for retention in its current form.</div>}
    </>}
    {error && <div role="alert" style={{ color: '#f59e0b', marginTop: 6 }}>{error}</div>}
  </div>;
}
